import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import toast from '../../../components/ui/Toast';
import { supabase } from '../../../lib/supabase';

const SMSReminderSettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    smsEnabled: false,
    phoneNumber: '',
    reminderTiming: {
      dayBefore: false,
      morningOf: false,
      hourBefore: false
    },
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    },
    taskTypes: {
      urgent: true,
      high: true,
      normal: false,
      low: false
    }
  });

  useEffect(() => {
    loadSettings();
  }, [userId]);

  const loadSettings = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('notification_preferences')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      
      if (data) {
        setSettings({
          smsEnabled: data?.sms_enabled || false,
          phoneNumber: data?.phone_number || '',
          reminderTiming: data?.reminder_timing || settings?.reminderTiming,
          quietHours: data?.quiet_hours || settings?.quietHours,
          taskTypes: data?.task_types || settings?.taskTypes
        });
      }
    } catch (error) {
      console.error('Error loading SMS settings:', error);
      toast?.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (settings?.smsEnabled && !settings?.phoneNumber) {
      toast?.error('Please enter a phone number');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('notification_preferences')
        ?.upsert({
          user_id: userId,
          sms_enabled: settings?.smsEnabled,
          phone_number: settings?.phoneNumber,
          reminder_timing: settings?.reminderTiming,
          quiet_hours: settings?.quietHours,
          task_types: settings?.taskTypes,
          updated_at: new Date()?.toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast?.success('SMS reminder settings saved successfully!');
    } catch (error) {
      console.error('Error saving SMS settings:', error);
      toast?.error(error?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTimingChange = (key, checked) => {
    setSettings(prev => ({
      ...prev,
      reminderTiming: {
        ...prev?.reminderTiming,
        [key]: checked
      }
    }));
  };

  const handleTaskTypeChange = (key, checked) => {
    setSettings(prev => ({
      ...prev,
      taskTypes: {
        ...prev?.taskTypes,
        [key]: checked
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin" color="var(--color-primary)" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Bell" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1">
              SMS Reminder Settings
            </h2>
            <p className="text-sm text-muted-foreground">
              Configure automated text message reminders for your tasks and deadlines
            </p>
          </div>
        </div>
      </div>

      {/* Enable SMS */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground mb-1">Enable SMS Reminders</h3>
            <p className="text-sm text-muted-foreground">
              Receive text message notifications for upcoming tasks
            </p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, smsEnabled: !prev?.smsEnabled }))}
            className={`
              relative w-12 h-6 rounded-full transition-colors duration-250
              ${settings?.smsEnabled ? 'bg-primary' : 'bg-muted'}
            `}
          >
            <div
              className={`
                absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-250
                ${settings?.smsEnabled ? 'translate-x-6' : 'translate-x-0.5'}
              `}
            />
          </button>
        </div>

        {settings?.smsEnabled && (
          <Input
            type="tel"
            label="Phone Number"
            description="Enter your phone number with country code (e.g., +1234567890)"
            placeholder="+1 (555) 123-4567"
            value={settings?.phoneNumber || ''}
            onChange={(e) => setSettings(prev => ({ ...prev, phoneNumber: e?.target?.value }))}
            required
          />
        )}
      </div>

      {/* Reminder Timing */}
      {settings?.smsEnabled && (
        <>
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="font-semibold text-foreground mb-3">Reminder Timing</h3>
            <p className="text-sm text-muted-foreground mb-4">
              When should we send you reminders?
            </p>
            <div className="space-y-3">
              <Checkbox
                label="Day Before"
                description="Reminder 24 hours before task deadline"
                checked={settings?.reminderTiming?.dayBefore}
                onChange={(e) => handleTimingChange('dayBefore', e?.target?.checked)}
              />
              <Checkbox
                label="Morning Of"
                description="Reminder at 8:00 AM on the day of deadline"
                checked={settings?.reminderTiming?.morningOf}
                onChange={(e) => handleTimingChange('morningOf', e?.target?.checked)}
              />
              <Checkbox
                label="One Hour Before"
                description="Reminder 1 hour before task deadline"
                checked={settings?.reminderTiming?.hourBefore}
                onChange={(e) => handleTimingChange('hourBefore', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Quiet Hours */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-foreground mb-1">Quiet Hours</h3>
                <p className="text-sm text-muted-foreground">
                  Pause notifications during specific hours
                </p>
              </div>
              <button
                onClick={() => setSettings(prev => ({
                  ...prev,
                  quietHours: { ...prev?.quietHours, enabled: !prev?.quietHours?.enabled }
                }))}
                className={`
                  relative w-12 h-6 rounded-full transition-colors duration-250
                  ${settings?.quietHours?.enabled ? 'bg-primary' : 'bg-muted'}
                `}
              >
                <div
                  className={`
                    absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-250
                    ${settings?.quietHours?.enabled ? 'translate-x-6' : 'translate-x-0.5'}
                  `}
                />
              </button>
            </div>

            {settings?.quietHours?.enabled && (
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  type="time"
                  label="Start Time"
                  value={settings?.quietHours?.start || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    quietHours: { ...prev?.quietHours, start: e?.target?.value }
                  }))}
                />
                <Input
                  type="time"
                  label="End Time"
                  value={settings?.quietHours?.end || ''}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    quietHours: { ...prev?.quietHours, end: e?.target?.value }
                  }))}
                />
              </div>
            )}
          </div>

          {/* Task Priority Filter */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="font-semibold text-foreground mb-3">Task Priority Filter</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Which task priorities should trigger SMS reminders?
            </p>
            <div className="space-y-3">
              <Checkbox
                label="Urgent Tasks"
                description="Critical tasks requiring immediate attention"
                checked={settings?.taskTypes?.urgent}
                onChange={(e) => handleTaskTypeChange('urgent', e?.target?.checked)}
              />
              <Checkbox
                label="High Priority Tasks"
                description="Important tasks with upcoming deadlines"
                checked={settings?.taskTypes?.high}
                onChange={(e) => handleTaskTypeChange('high', e?.target?.checked)}
              />
              <Checkbox
                label="Normal Priority Tasks"
                description="Standard tasks with regular deadlines"
                checked={settings?.taskTypes?.normal}
                onChange={(e) => handleTaskTypeChange('normal', e?.target?.checked)}
              />
              <Checkbox
                label="Low Priority Tasks"
                description="Non-urgent tasks with flexible deadlines"
                checked={settings?.taskTypes?.low}
                onChange={(e) => handleTaskTypeChange('low', e?.target?.checked)}
              />
            </div>
          </div>
        </>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2"
        >
          {saving ? (
            <>
              <Icon name="Loader2" size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Icon name="Save" size={18} />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SMSReminderSettings;